import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { FileText, Download, Clock, DollarSign, Users, CalendarIcon, TrendingUp, AlertTriangle, CheckCircle, XCircle, UserCheck, Printer, Factory, Activity, ShieldAlert } from 'lucide-react';
import { openLetterheadWindow } from '../utils/brandedPrint';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const ReportsPage = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [report, setReport] = useState(null);
  const [activeTab, setActiveTab] = useState('time');
  const [rosterData, setRosterData] = useState(null);
  const [overtimeAlerts, setOvertimeAlerts] = useState([]);
  const [shiftAssignments, setShiftAssignments] = useState([]);

  const [filters, setFilters] = useState({
    start_date: '',
    end_date: '',
    user_id: '',
    work_order_id: '',
  });

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // iter34 — analytics data
  const [productivity, setProductivity] = useState(null);
  const [churn, setChurn] = useState(null);
  const [churnDays, setChurnDays] = useState(30);
  const [prodRate, setProdRate] = useState(null);
  const [boardPack, setBoardPack] = useState(null);
  const [boardLoading, setBoardLoading] = useState(false);

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      const [usersRes, woRes, assignRes, overtimeRes] = await Promise.all([
        api().get('/users').catch(() => ({ data: [] })),
        api().get('/work-orders'),
        api().get('/shift-assignments').catch(() => ({ data: [] })),
        api().get('/shifts/overtime/check').catch(() => ({ data: { overtime_alerts: [] } }))
      ]);
      setUsers(usersRes.data);
      setWorkOrders(woRes.data);
      setShiftAssignments(assignRes.data);
      setOvertimeAlerts(overtimeRes.data.overtime_alerts || []);
      
      // Build roster data
      buildRosterData(usersRes.data, assignRes.data);
    } catch (error) {
      console.error('Failed to fetch initial data');
    }
  };

  // iter34 — analytics loaders
  const loadProductivity = async () => {
    try {
      const res = await api().get('/reports/productivity');
      setProductivity(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load productivity'); }
  };
  const loadChurn = async (days = churnDays) => {
    try {
      const res = await api().get(`/reports/inventory-churn?days=${days}`);
      setChurn(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load churn'); }
  };
  const loadProdRate = async () => {
    try {
      const res = await api().get('/dashboard/production-progress?period_type=weekly');
      setProdRate(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load production rate'); }
  };
  const loadBoardPack = async () => {
    setBoardLoading(true);
    try {
      const res = await api().get('/reports/board-pack');
      setBoardPack(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load board pack'); }
    finally { setBoardLoading(false); }
  };

  useEffect(() => {
    if (activeTab === 'productivity' && !productivity) loadProductivity();
    if (activeTab === 'churn' && !churn) loadChurn();
    if (activeTab === 'rate' && !prodRate) loadProdRate();
    if (activeTab === 'board' && !boardPack) loadBoardPack();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const printBoardPack = async () => {
    if (!boardPack) return;
    const b = boardPack;
    const fmt = (n) => (typeof n === 'number' ? n.toLocaleString() : n ?? '—');
    const money = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const windowLbl = `${b.window?.start || ''} → ${b.window?.end || ''}`;
    const productionPct = b.production?.totals?.progress_percent ?? 0;
    const html = `<!doctype html><html><head><title>Board Pack — ${windowLbl}</title>
      <style>
        @page { size: Letter; margin: 0.6in; }
        body { font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; color: #111; line-height: 1.45; }
        h1 { font-size: 20px; margin: 0; letter-spacing: -0.02em; }
        h2 { font-size: 12px; margin: 20px 0 6px; text-transform: uppercase; letter-spacing: 0.12em; color: #475569; border-bottom: 1px solid #cbd5e1; padding-bottom: 4px; }
        .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #0f172a; padding-bottom: 10px; }
        .header .logo { max-height: 56px; max-width: 200px; object-fit: contain; }
        .meta { text-align: right; font-size: 11px; color: #666; }
        .banner { background: #0f172a; color: white; padding: 6px 10px; margin-top: 6px; font-size: 11px; letter-spacing: 0.1em; text-transform: uppercase; }
        .kpis { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin: 14px 0; }
        .kpi { border: 1px solid #e2e8f0; padding: 10px; border-radius: 4px; background: #f8fafc; }
        .kpi .v { font-size: 22px; font-weight: 700; color: #0f172a; }
        .kpi .l { font-size: 9px; color: #64748b; text-transform: uppercase; letter-spacing: 0.1em; margin-top: 2px; }
        table { width: 100%; border-collapse: collapse; margin: 6px 0 14px; }
        th, td { padding: 5px 8px; border-bottom: 1px solid #e5e7eb; text-align: left; font-size: 11px; }
        th { background: #f1f5f9; font-weight: 600; color: #334155; text-transform: uppercase; letter-spacing: 0.08em; font-size: 9px; }
        .right { text-align: right; }
        .bar { height: 8px; background: #e2e8f0; border-radius: 4px; overflow: hidden; margin-top: 4px; }
        .bar > div { height: 100%; background: linear-gradient(90deg, #10b981, #059669); }
        .section-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; }
        .footer { margin-top: 28px; font-size: 9px; color: #94a3b8; border-top: 1px solid #e2e8f0; padding-top: 6px; }
        .coming { background: #ecfeff; border-left: 3px solid #06b6d4; padding: 6px 10px; font-size: 10px; color: #155e75; margin-top: 10px; font-style: italic; }
      </style></head><body>
      <div class="header">
        <div><!--LH--></div>
        <div class="meta">
          <div><strong>Board Pack</strong></div>
          <div>${windowLbl}</div>
          <div>Generated ${new Date(b.generated_at).toLocaleString()}</div>
          <div>By ${b.generated_by || ''}</div>
        </div>
      </div>
      ${b.branding?.header_note ? `<div class="banner">${b.branding.header_note}</div>` : ''}
      <div class="kpis">
        <div class="kpi"><div class="v">${b.production?.totals?.completed_total ?? 0}<span style="font-size:13px;color:#64748b;"> / ${b.production?.totals?.target_total ?? '—'}</span></div><div class="l">Units Built vs Target</div><div class="bar"><div style="width:${Math.min(100, productionPct)}%"></div></div></div>
        <div class="kpi"><div class="v">${b.hours?.payable_hours ?? 0}h<span style="font-size:12px;color:#64748b;"> + ${b.hours?.non_payable_hours ?? 0}h NP</span></div><div class="l">Payable Hours this week</div></div>
        <div class="kpi"><div class="v" style="color:${b.recalls?.active_recall_count ? '#b91c1c' : '#16a34a'}">${b.recalls?.active_recall_count ?? 0}</div><div class="l">Active Recalls · ${b.recalls?.flagged_unit_count ?? 0} units flagged</div></div>
        <div class="kpi"><div class="v">${money(b.low_stock?.total_dollar_impact || 0)}</div><div class="l">Low-Stock $ Impact · ${b.low_stock?.count || 0} SKU(s)</div></div>
      </div>
      <div class="section-grid">
        <div>
          <h2>Production — Top Product Types</h2>
          <table>
            <tr><th>Product</th><th class="right">Built</th><th class="right">Target</th><th class="right">%</th></tr>
            ${(b.production?.product_breakdown || []).slice(0, 6).map(p => `<tr><td>${p.product_type_name || p.product_type_id}</td><td class="right">${fmt(p.completed)}</td><td class="right">${fmt(p.target || '—')}</td><td class="right">${p.progress_percent != null ? p.progress_percent + '%' : '—'}</td></tr>`).join('') || '<tr><td colspan="4" style="text-align:center;color:#94a3b8;">No production activity</td></tr>'}
          </table>
          <h2>Top Assemblers · Hours this Week</h2>
          <table>
            <tr><th>User</th><th class="right">Payable</th><th class="right">Total</th></tr>
            ${(b.hours?.top_users || []).map(u => `<tr><td>${u.name}</td><td class="right">${u.payable_hours}h</td><td class="right">${u.total_hours}h</td></tr>`).join('') || '<tr><td colspan="3" style="text-align:center;color:#94a3b8;">No hours logged</td></tr>'}
          </table>
        </div>
        <div>
          <h2>Low-Stock · Top 5 by $ Impact</h2>
          <table>
            <tr><th>SKU</th><th class="right">Needed</th><th class="right">$ Impact</th></tr>
            ${(b.low_stock?.top || []).map(r => `<tr><td>${r.item_sku}<div style="font-size:9px;color:#64748b;">${r.item_name || ''}</div></td><td class="right">${r.qty_needed}</td><td class="right">${money(r.dollar_impact)}</td></tr>`).join('') || '<tr><td colspan="3" style="text-align:center;color:#94a3b8;">All SKUs above reorder point</td></tr>'}
          </table>
          <h2>BOM Review Queue</h2>
          <table>
            <tr><th>BOM</th><th>Rev</th><th class="right">Due</th></tr>
            ${(b.bom_review?.items || []).slice(0, 5).map(r => `<tr><td>${r.bom_name || r.bom_id}</td><td>${r.revision_number || ''}</td><td class="right">${r.days_until_due != null ? r.days_until_due + 'd' : '—'}</td></tr>`).join('') || `<tr><td colspan="3" style="text-align:center;color:#94a3b8;">No BOMs due in 30 days</td></tr>`}
          </table>
          <h2>Shipping — This Week</h2>
          <div style="font-size:11px; color:#334155;">${b.shipping?.bols_issued_this_week || 0} BoL(s) issued · ${b.shipping?.units_shipped_this_week || 0} unit(s) shipped</div>
          ${(b.shipping?.recent_bols || []).length > 0 ? `<table><tr><th>BoL #</th><th>Consignee</th></tr>${b.shipping.recent_bols.map(x => `<tr><td>${x.bol_number}</td><td>${x.consignee_name || '—'}</td></tr>`).join('')}</table>` : ''}
        </div>
      </div>
      <div class="coming">Coming soon: this pack will be auto-emailed every Monday at 7am to an admin-configured distribution list.</div>
      <script>window.onload = () => setTimeout(() => window.print(), 400);</script>
      </body></html>`;
    const w = await openLetterheadWindow(html, { features: 'width=900,height=1200' });
    if (!w) { toast.error('Pop-ups blocked. Allow pop-ups to print the board pack.'); return; }
  };

  const buildRosterData = async (usersData, assignmentsData) => {
    try {
      // Get active time entries
      const activeEntries = await Promise.all(
        usersData.map(async (user) => {
          try {
            const res = await api().get('/time-entries/my-entries?limit=1');
            const latestEntry = res.data.find(e => e.is_active);
            return { user_id: user.id, activeEntry: latestEntry || null };
          } catch {
            return { user_id: user.id, activeEntry: null };
          }
        })
      );

      const roster = usersData.map(user => {
        const assignment = assignmentsData.find(a => a.user_id === user.id);
        const activeEntry = activeEntries.find(e => e.user_id === user.id);
        return {
          ...user,
          shift_name: assignment?.shift_name || 'No Shift',
          shift_details: assignment?.shift_details || null,
          is_clocked_in: !!activeEntry?.activeEntry,
          active_work_order: activeEntry?.activeEntry?.work_order_id || null,
        };
      });
      
      setRosterData(roster);
    } catch (error) {
      console.error('Failed to build roster data');
    }
  };

  const generateReport = async () => {
    setLoading(true);
    try {
      const payload = {
        start_date: startDate ? format(startDate, 'yyyy-MM-dd') : null,
        end_date: endDate ? format(endDate, 'yyyy-MM-dd') : null,
        user_id: filters.user_id || null,
        work_order_id: filters.work_order_id || null,
      };
      const res = await api().post('/reports/time', payload);
      setReport(res.data);
      toast.success('Report generated');
    } catch (error) {
      toast.error('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const exportCSV = async () => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', format(startDate, 'yyyy-MM-dd'));
      if (endDate) params.append('end_date', format(endDate, 'yyyy-MM-dd'));
      params.append('format', 'csv');

      const res = await api().get(`/reports/time/export?${params.toString()}`);
      
      // Create and download CSV file
      const blob = new Blob([res.data.csv_content], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = res.data.filename;
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast.success('Report exported');
    } catch (error) {
      toast.error('Failed to export report');
    }
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '0h 0m';
    const hours = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return `${hours}h ${mins}m`;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: report?.summary?.currency || 'USD' }).format(amount);
  };

  return (
    <div className="space-y-6 animate-slideIn" data-testid="reports-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Reports</h1>
          <p className="text-slate-500 text-sm">Time tracking reports and labor cost analysis</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="time" data-testid="time-report-tab">
            <Clock className="w-4 h-4 mr-2" />
            Time Reports
          </TabsTrigger>
          <TabsTrigger value="roster" data-testid="roster-tab">
            <UserCheck className="w-4 h-4 mr-2" />
            Roster
          </TabsTrigger>
          <TabsTrigger value="labor" data-testid="labor-report-tab">
            <DollarSign className="w-4 h-4 mr-2" />
            Labor Costs
          </TabsTrigger>
          <TabsTrigger value="board" data-testid="board-pack-tab">
            <FileText className="w-4 h-4 mr-2" />
            Board Pack
          </TabsTrigger>
          <TabsTrigger value="productivity" data-testid="productivity-tab">
            <Activity className="w-4 h-4 mr-2" />
            Productivity
          </TabsTrigger>
          <TabsTrigger value="churn" data-testid="churn-tab">
            <TrendingUp className="w-4 h-4 mr-2" />
            Inventory Churn
          </TabsTrigger>
          <TabsTrigger value="rate" data-testid="rate-tab">
            <Factory className="w-4 h-4 mr-2" />
            Production Rate
          </TabsTrigger>
        </TabsList>

        <TabsContent value="time" className="space-y-6">
          {/* Filters */}
          <Card className="industrial-card">
            <CardHeader className="border-b border-slate-100 pb-3">
              <CardTitle className="text-lg">Report Filters</CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start rounded-sm">
                        <CalendarIcon className="w-4 h-4 mr-2" />
                        {startDate ? format(startDate, 'MMM dd, yyyy') : 'Select'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={startDate}
                        onSelect={setStartDate}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start rounded-sm">
                        <CalendarIcon className="w-4 h-4 mr-2" />
                        {endDate ? format(endDate, 'MMM dd, yyyy') : 'Select'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={endDate}
                        onSelect={setEndDate}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label>User</Label>
                  <Select value={filters.user_id || 'all'} onValueChange={(v) => setFilters({ ...filters, user_id: v === 'all' ? '' : v })}>
                    <SelectTrigger className="rounded-sm">
                      <SelectValue placeholder="All Users" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Users</SelectItem>
                      {users.map(u => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Work Order</Label>
                  <Select value={filters.work_order_id || 'all'} onValueChange={(v) => setFilters({ ...filters, work_order_id: v === 'all' ? '' : v })}>
                    <SelectTrigger className="rounded-sm">
                      <SelectValue placeholder="All Work Orders" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Work Orders</SelectItem>
                      {workOrders.map(wo => <SelectItem key={wo.id} value={wo.id}>WO-{wo.id.slice(0, 8)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end gap-2">
                  <Button onClick={generateReport} disabled={loading} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="generate-report-btn">
                    {loading ? <span className="spinner" /> : <FileText className="w-4 h-4 mr-2" />}
                    Generate
                  </Button>
                  {report && (
                    <Button onClick={exportCSV} variant="outline" className="rounded-sm" data-testid="export-csv-btn">
                      <Download className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Report Results */}
          {report && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <Card className="industrial-card">
                  <CardContent className="p-4 text-center">
                    <Clock className="w-6 h-6 mx-auto mb-2 text-blue-600" />
                    <p className="text-2xl font-bold mono">{report.summary.total_hours}h</p>
                    <p className="text-xs text-slate-500 uppercase">Total Hours</p>
                  </CardContent>
                </Card>
                <Card className="industrial-card">
                  <CardContent className="p-4 text-center">
                    <FileText className="w-6 h-6 mx-auto mb-2 text-purple-600" />
                    <p className="text-2xl font-bold mono">{report.summary.total_entries}</p>
                    <p className="text-xs text-slate-500 uppercase">Time Entries</p>
                  </CardContent>
                </Card>
                <Card className="industrial-card">
                  <CardContent className="p-4 text-center">
                    <TrendingUp className="w-6 h-6 mx-auto mb-2 text-green-600" />
                    <p className="text-2xl font-bold mono">{report.summary.total_units}</p>
                    <p className="text-xs text-slate-500 uppercase">Units Completed</p>
                  </CardContent>
                </Card>
                <Card className="industrial-card">
                  <CardContent className="p-4 text-center">
                    <DollarSign className="w-6 h-6 mx-auto mb-2 text-orange-600" />
                    <p className="text-2xl font-bold mono">{formatCurrency(report.summary.total_labor_cost)}</p>
                    <p className="text-xs text-slate-500 uppercase">Labor Cost</p>
                  </CardContent>
                </Card>
                <Card className="industrial-card">
                  <CardContent className="p-4 text-center">
                    <Users className="w-6 h-6 mx-auto mb-2 text-indigo-600" />
                    <p className="text-2xl font-bold mono">{report.by_user.length}</p>
                    <p className="text-xs text-slate-500 uppercase">Unique Users</p>
                  </CardContent>
                </Card>
              </div>

              {/* By User */}
              <Card className="industrial-card">
                <CardHeader className="border-b border-slate-100 pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Users className="w-5 h-5 text-indigo-600" />
                    Time by User
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <Table className="data-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>User</TableHead>
                        <TableHead>Entries</TableHead>
                        <TableHead>Hours</TableHead>
                        <TableHead>Units</TableHead>
                        <TableHead>Labor Cost</TableHead>
                        <TableHead>Avg Hours/Entry</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {report.by_user.map((row) => (
                        <TableRow key={row.user_id}>
                          <TableCell className="font-medium">{row.user_name}</TableCell>
                          <TableCell className="mono">{row.entries}</TableCell>
                          <TableCell className="mono">{(row.minutes / 60).toFixed(1)}h</TableCell>
                          <TableCell className="mono">{row.units}</TableCell>
                          <TableCell className="mono font-semibold">{formatCurrency(row.cost)}</TableCell>
                          <TableCell className="mono text-slate-500">{(row.minutes / 60 / row.entries).toFixed(1)}h</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {/* By Date */}
              <Card className="industrial-card">
                <CardHeader className="border-b border-slate-100 pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <CalendarIcon className="w-5 h-5 text-green-600" />
                    Time by Date
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <Table className="data-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Entries</TableHead>
                        <TableHead>Hours</TableHead>
                        <TableHead>Units</TableHead>
                        <TableHead>Labor Cost</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {report.by_date.slice(0, 14).map((row) => (
                        <TableRow key={row.date}>
                          <TableCell className="font-medium">{row.date}</TableCell>
                          <TableCell className="mono">{row.entries}</TableCell>
                          <TableCell className="mono">{(row.minutes / 60).toFixed(1)}h</TableCell>
                          <TableCell className="mono">{row.units}</TableCell>
                          <TableCell className="mono font-semibold">{formatCurrency(row.cost)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Roster Tab */}
        <TabsContent value="roster" className="space-y-6">
          {/* Overtime Alerts */}
          {overtimeAlerts.length > 0 && (
            <Card className="border-orange-200 bg-orange-50">
              <CardHeader className="border-b border-orange-200 pb-3">
                <CardTitle className="text-lg flex items-center gap-2 text-orange-800">
                  <AlertTriangle className="w-5 h-5" />
                  Overtime Alerts ({overtimeAlerts.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <div className="space-y-2">
                  {overtimeAlerts.map((alert, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-white p-3 rounded-sm border border-orange-200">
                      <div>
                        <p className="font-medium">{alert.user_name}</p>
                        <p className="text-sm text-slate-500">Shift: {alert.shift_name}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-orange-600">{alert.overtime_hours}h OT</p>
                        <p className="text-sm text-slate-500">Total: {alert.total_hours}h</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Roster Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="industrial-card">
              <CardContent className="p-4 text-center">
                <Users className="w-6 h-6 mx-auto mb-2 text-blue-600" />
                <p className="text-2xl font-bold mono">{rosterData?.length || 0}</p>
                <p className="text-xs text-slate-500 uppercase">Total Staff</p>
              </CardContent>
            </Card>
            <Card className="industrial-card">
              <CardContent className="p-4 text-center">
                <CheckCircle className="w-6 h-6 mx-auto mb-2 text-green-600" />
                <p className="text-2xl font-bold mono">{rosterData?.filter(r => r.is_clocked_in).length || 0}</p>
                <p className="text-xs text-slate-500 uppercase">Clocked In</p>
              </CardContent>
            </Card>
            <Card className="industrial-card">
              <CardContent className="p-4 text-center">
                <XCircle className="w-6 h-6 mx-auto mb-2 text-red-600" />
                <p className="text-2xl font-bold mono">{rosterData?.filter(r => !r.is_clocked_in).length || 0}</p>
                <p className="text-xs text-slate-500 uppercase">Not Clocked In</p>
              </CardContent>
            </Card>
            <Card className="industrial-card">
              <CardContent className="p-4 text-center">
                <AlertTriangle className="w-6 h-6 mx-auto mb-2 text-orange-600" />
                <p className="text-2xl font-bold mono">{overtimeAlerts.length}</p>
                <p className="text-xs text-slate-500 uppercase">Overtime</p>
              </CardContent>
            </Card>
          </div>

          {/* Staff Roster Table */}
          <Card className="industrial-card">
            <CardHeader className="border-b border-slate-100 pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <UserCheck className="w-5 h-5 text-indigo-600" />
                  Staff Roster
                </CardTitle>
                <Button variant="outline" size="sm" onClick={fetchInitialData} className="rounded-sm">
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {rosterData ? (
                <Table className="data-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Assigned Shift</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Active Work Order</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rosterData.map((user) => (
                      <TableRow key={user.id} className={user.is_clocked_in ? 'bg-green-50' : ''}>
                        <TableCell className="font-medium">{user.full_name}</TableCell>
                        <TableCell>
                          <span className="status-badge status-draft">{user.role}</span>
                        </TableCell>
                        <TableCell>
                          {user.shift_details ? (
                            <div>
                              <p className="font-medium">{user.shift_name}</p>
                              <p className="text-xs text-slate-500">{user.shift_details.start_time} - {user.shift_details.end_time}</p>
                            </div>
                          ) : (
                            <span className="text-slate-400">No shift assigned</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {user.is_clocked_in ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-sm text-sm">
                              <CheckCircle className="w-3 h-3" />
                              Clocked In
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-500 rounded-sm text-sm">
                              <XCircle className="w-3 h-3" />
                              Not Working
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          {user.active_work_order ? (
                            <span className="mono text-sm">WO-{user.active_work_order.slice(0, 8)}</span>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="flex items-center justify-center h-32">
                  <div className="spinner" />
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="labor" className="space-y-6">
          <Card className="industrial-card">
            <CardHeader className="border-b border-slate-100 pb-3">
              <CardTitle className="text-lg">Labor Cost Analysis</CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <p className="text-slate-500">
                Generate a time report first to see labor cost breakdown. Labor costs are calculated based on user hourly rates configured in Admin Settings.
              </p>
              {report && (
                <div className="mt-4 grid md:grid-cols-2 gap-4">
                  <div className="bg-slate-50 p-4 rounded-sm">
                    <p className="text-sm text-slate-500">Total Labor Cost</p>
                    <p className="text-3xl font-bold text-green-600">{formatCurrency(report.summary.total_labor_cost)}</p>
                  </div>
                  <div className="bg-slate-50 p-4 rounded-sm">
                    <p className="text-sm text-slate-500">Cost per Unit</p>
                    <p className="text-3xl font-bold text-blue-600">
                      {report.summary.total_units > 0 
                        ? formatCurrency(report.summary.total_labor_cost / report.summary.total_units)
                        : '-'}
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* BOARD PACK (iter34) */}
        <TabsContent value="board" className="space-y-4" data-testid="board-pack-panel">
          <Card className="industrial-card">
            <CardHeader className="border-b border-slate-100 pb-3 flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg">Board Pack — Monday Morning Summary</CardTitle>
                <p className="text-xs text-slate-500">One-click executive summary: production vs target, payable hours, recalls, BOM queue, top low-stock, shipping activity.</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => { setBoardPack(null); loadBoardPack(); }} data-testid="board-refresh-btn"><TrendingUp className="w-4 h-4 mr-1" /> Refresh</Button>
                <Button className="bg-slate-900 hover:bg-slate-800 rounded-sm" size="sm" onClick={printBoardPack} disabled={!boardPack || boardLoading} data-testid="board-print-btn"><Printer className="w-4 h-4 mr-1" /> Print / PDF</Button>
              </div>
            </CardHeader>
            <CardContent className="p-4">
              {boardLoading && <p className="text-center text-slate-400 py-8">Loading board pack…</p>}
              {!boardLoading && boardPack && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div className="p-3 rounded-sm bg-slate-50 border border-slate-200">
                      <div className="text-2xl font-bold text-slate-900">{boardPack.production?.totals?.completed_total ?? 0}<span className="text-sm text-slate-500"> / {boardPack.production?.totals?.target_total ?? '—'}</span></div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">Units Built vs Target</div>
                      <div className="h-1.5 mt-2 bg-slate-200 rounded overflow-hidden"><div className="h-full bg-emerald-500" style={{ width: `${Math.min(100, boardPack.production?.totals?.progress_percent || 0)}%` }} /></div>
                    </div>
                    <div className="p-3 rounded-sm bg-slate-50 border border-slate-200">
                      <div className="text-2xl font-bold text-slate-900">{boardPack.hours?.payable_hours ?? 0}<span className="text-sm text-slate-500">h + {boardPack.hours?.non_payable_hours ?? 0}h NP</span></div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">Payable Hours this Week</div>
                    </div>
                    <div className={`p-3 rounded-sm border ${boardPack.recalls?.active_recall_count ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'}`}>
                      <div className={`text-2xl font-bold ${boardPack.recalls?.active_recall_count ? 'text-red-700' : 'text-emerald-700'}`}>{boardPack.recalls?.active_recall_count ?? 0}</div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">Active Recalls · {boardPack.recalls?.flagged_unit_count ?? 0} units</div>
                    </div>
                    <div className="p-3 rounded-sm bg-slate-50 border border-slate-200">
                      <div className="text-2xl font-bold text-slate-900">${(boardPack.low_stock?.total_dollar_impact || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">Low-Stock $ Impact · {boardPack.low_stock?.count || 0} SKU(s)</div>
                    </div>
                  </div>
                  <div className="bg-cyan-50 border-l-4 border-cyan-500 text-cyan-900 px-4 py-2 text-sm rounded-sm">
                    <strong>Coming soon:</strong> this pack will auto-email every Monday at 7am to an admin-configured distribution list.
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* PRODUCTIVITY (iter34) */}
        <TabsContent value="productivity" className="space-y-4" data-testid="productivity-panel">
          <Card className="industrial-card">
            <CardHeader className="border-b border-slate-100 pb-3">
              <CardTitle className="text-lg">Employee Productivity · This Week vs Last Week</CardTitle>
              <p className="text-xs text-slate-500">Payable hours + total hours (incl. PTO/sick) side-by-side. Finished units + QC submissions.</p>
            </CardHeader>
            <CardContent className="p-0">
              {!productivity && <p className="text-center text-slate-400 py-8">Loading…</p>}
              {productivity && (
                <>
                  <div className="p-4">
                    <ResponsiveContainer width="100%" height={240}>
                      <BarChart data={productivity.users.slice(0, 12).map(u => ({ name: u.name.split(' ')[0], payable: u.current.payable_hours, nonPayable: Math.max(0, u.current.total_hours - u.current.payable_hours), previous: u.previous.total_hours }))}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} label={{ value: 'Hours', angle: -90, position: 'insideLeft', style: { fontSize: 11 } }} />
                        <Tooltip />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Bar dataKey="payable" stackId="cur" fill="#10b981" name="Payable (cur)" />
                        <Bar dataKey="nonPayable" stackId="cur" fill="#94a3b8" name="Non-Payable (cur)" />
                        <Bar dataKey="previous" fill="#cbd5e1" name="Previous week (total)" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <Table className="data-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>User</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead className="text-right">Payable h</TableHead>
                        <TableHead className="text-right">Total h</TableHead>
                        <TableHead className="text-right">Δ h</TableHead>
                        <TableHead className="text-right">Finished Units</TableHead>
                        <TableHead className="text-right">QC ✓/✗</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {productivity.users.map(u => (
                        <TableRow key={u.user_id} data-testid={`productivity-row-${u.user_id}`}>
                          <TableCell className="font-medium">{u.name}</TableCell>
                          <TableCell className="text-xs text-slate-500">{u.role}</TableCell>
                          <TableCell className="text-right mono">{u.current.payable_hours}</TableCell>
                          <TableCell className="text-right mono">{u.current.total_hours}</TableCell>
                          <TableCell className={`text-right mono ${u.delta_hours > 0 ? 'text-emerald-600' : u.delta_hours < 0 ? 'text-red-600' : 'text-slate-400'}`}>{u.delta_hours > 0 ? '+' : ''}{u.delta_hours}</TableCell>
                          <TableCell className="text-right mono">{u.current.finished_units} <span className="text-slate-400">(prev {u.previous.finished_units})</span></TableCell>
                          <TableCell className="text-right text-xs"><span className="text-emerald-600">{u.current.qc_pass}</span>/<span className="text-red-600">{u.current.qc_fail}</span></TableCell>
                        </TableRow>
                      ))}
                      {productivity.users.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-6 text-slate-400">No user activity this week yet.</TableCell></TableRow>}
                    </TableBody>
                  </Table>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* INVENTORY CHURN (iter34) */}
        <TabsContent value="churn" className="space-y-4" data-testid="churn-panel">
          <Card className="industrial-card">
            <CardHeader className="border-b border-slate-100 pb-3 flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg">Inventory Churn · Receipts vs Consumption vs Disposals</CardTitle>
                <p className="text-xs text-slate-500">Last {churnDays} days. Top 10 items by total movement.</p>
              </div>
              <Select value={String(churnDays)} onValueChange={(v) => { setChurnDays(Number(v)); setChurn(null); loadChurn(Number(v)); }}>
                <SelectTrigger className="w-28" data-testid="churn-window-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="30">30 days</SelectItem>
                  <SelectItem value="60">60 days</SelectItem>
                  <SelectItem value="90">90 days</SelectItem>
                </SelectContent>
              </Select>
            </CardHeader>
            <CardContent className="p-0">
              {!churn && <p className="text-center text-slate-400 py-8">Loading…</p>}
              {churn && (
                <>
                  <div className="p-4">
                    <ResponsiveContainer width="100%" height={240}>
                      <LineChart data={churn.day_series}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 10 }} />
                        <Tooltip />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Line type="monotone" dataKey="received" stroke="#10b981" strokeWidth={2} name="Received" dot={false} />
                        <Line type="monotone" dataKey="consumed" stroke="#f97316" strokeWidth={2} name="Consumed" dot={false} />
                        <Line type="monotone" dataKey="disposed" stroke="#ef4444" strokeWidth={2} name="Disposed" dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  <Table className="data-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>SKU</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead className="text-right">Received</TableHead>
                        <TableHead className="text-right">Consumed</TableHead>
                        <TableHead className="text-right">Disposed</TableHead>
                        <TableHead className="text-right">Net Δ</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {churn.top_items.slice(0, 10).map(r => (
                        <TableRow key={r.item_sku} data-testid={`churn-row-${r.item_sku}`}>
                          <TableCell className="mono text-xs">{r.item_sku}</TableCell>
                          <TableCell className="text-xs">{r.item_name || '—'}</TableCell>
                          <TableCell className="text-right mono text-emerald-600">{r.received}</TableCell>
                          <TableCell className="text-right mono text-orange-600">{r.consumed}</TableCell>
                          <TableCell className="text-right mono text-red-600">{r.disposed}</TableCell>
                          <TableCell className={`text-right mono ${r.net_change > 0 ? 'text-emerald-600' : r.net_change < 0 ? 'text-red-600' : 'text-slate-500'}`}>{r.net_change > 0 ? '+' : ''}{r.net_change}</TableCell>
                        </TableRow>
                      ))}
                      {churn.top_items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-6 text-slate-400">No inventory movement in this window.</TableCell></TableRow>}
                    </TableBody>
                  </Table>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* PRODUCTION RATE (iter34) */}
        <TabsContent value="rate" className="space-y-4" data-testid="rate-panel">
          <Card className="industrial-card">
            <CardHeader className="border-b border-slate-100 pb-3">
              <CardTitle className="text-lg">Production Rate · Builds per Day</CardTitle>
              <p className="text-xs text-slate-500">Weekly window · per product-type breakdown.</p>
            </CardHeader>
            <CardContent className="p-4">
              {!prodRate && <p className="text-center text-slate-400 py-8">Loading…</p>}
              {prodRate && (
                <>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={prodRate.day_series}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="completed" fill="#f97316" name="Units built" />
                    </BarChart>
                  </ResponsiveContainer>
                  <div className="mt-4">
                    <h3 className="text-sm font-semibold text-slate-700 mb-2">Per Product Type</h3>
                    <Table className="data-table">
                      <TableHeader>
                        <TableRow><TableHead>Product</TableHead><TableHead className="text-right">Built</TableHead><TableHead className="text-right">Target</TableHead><TableHead className="text-right">%</TableHead></TableRow>
                      </TableHeader>
                      <TableBody>
                        {(prodRate.product_breakdown || []).map(p => (
                          <TableRow key={p.product_type_id} data-testid={`rate-row-${p.product_type_id}`}>
                            <TableCell>{p.product_type_name}</TableCell>
                            <TableCell className="text-right mono">{p.completed}</TableCell>
                            <TableCell className="text-right mono">{p.target || '—'}</TableCell>
                            <TableCell className="text-right mono">{p.progress_percent != null ? p.progress_percent + '%' : '—'}</TableCell>
                          </TableRow>
                        ))}
                        {(!prodRate.product_breakdown || prodRate.product_breakdown.length === 0) && <TableRow><TableCell colSpan={4} className="text-center py-6 text-slate-400">No production activity this week.</TableCell></TableRow>}
                      </TableBody>
                    </Table>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ReportsPage;
